import { ApplicationInsights, DistributedTracingModes, PageView, PageViewPerformance, type ITelemetryItem } from '@microsoft/applicationinsights-web'
import { onLCP, onINP, onCLS, type Metric } from 'web-vitals'
import { libellePageCourant } from './titrePage'

export type FrontendAuthEvent =
  | 'auth_login_success'
  | 'auth_access_denied_403'
  // Issue #43 — échec au retour de redirection Entra. Sans lui, un client bloqué ne laisse AUCUNE
  // trace côté Pragmelis : l'échec précède tout appel API. `reason` porte le code AADSTS.
  | 'auth_redirect_error'

export interface FrontendAuthEventProps {
  accountId?: string
  userId?: string
  role?: string
  reason?: string
}

let appInsights: ApplicationInsights | null = null
// accountId interne (GUID portail) posé après résolution du profil — corrélation, JAMAIS de PII.
let currentAccountId: string | undefined

/**
 * Domaine de l'API pour la corrélation cross-origin (W3C `traceparent`), dérivé de `VITE_API_BASE_URL`.
 * Vide en local (proxy Vite, same-origin) → fallback `localhost:7071` pour tester la corrélation en dev.
 */
function correlationDomains(): string[] | undefined {
  const base = import.meta.env['VITE_API_BASE_URL'] as string | undefined
  if (base) {
    try {
      return [new URL(base).hostname]
    } catch {
      return undefined
    }
  }
  return import.meta.env.DEV ? ['localhost'] : undefined
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Réserve `O-DEV-6` — forme d'une connection string App Insights.
 *
 * La valeur est injectée au build par `Infra/deploy-frontend.ps1`, qui la lit via l'Azure CLI. Le
 * 2026-07-23, sur la construction de `dev`, une extension `az` absente avait fait écrire son invite
 * d'installation sur stdout : la « connection string » était en réalité une phrase, et le portail ne
 * démarrait plus. On valide donc la forme avant de tenter l'initialisation.
 *
 * Attendu : des paires `clé=valeur` séparées par `;`, dont un `InstrumentationKey` qui est un GUID.
 */
export function isValidConnectionString(raw: string): boolean {
  const key = raw
    .split(';')
    .map((part) => part.split('='))
    .find((pair) => pair[0]?.trim().toLowerCase() === 'instrumentationkey')?.[1]
  return key !== undefined && GUID_RE.test(key.trim())
}

export function initTelemetry(): void {
  const connStr = import.meta.env['VITE_APPLICATIONINSIGHTS_CONNECTION_STRING'] as string | undefined
  if (!connStr) return

  // `O-DEV-6` — un build sans télémétrie vaut mieux qu'un build qui ne démarre pas. `main.tsx` appelle
  // cette fonction avant le premier rendu : toute exception ici laisse l'utilisateur sur une page
  // blanche, sans message. On renonce à la télémétrie, jamais à l'application.
  if (!isValidConnectionString(connStr)) {
    console.warn(
      '[telemetry] VITE_APPLICATIONINSIGHTS_CONNECTION_STRING mal formée — télémétrie désactivée.'
    )
    return
  }

  try {
    initAppInsights(connStr)
  } catch (err) {
    appInsights = null
    console.warn('[telemetry] initialisation impossible — télémétrie désactivée.', err)
    return
  }

  trackWebVitals()
}

/** Construction effective du client App Insights. Peut lever — l'appelant garde le rendu en vie. */
function initAppInsights(connStr: string): void {
  const domains = correlationDomains()
  const instance = new ApplicationInsights({
    config: {
      connectionString: connStr,
      // Spec 081 (RUM) — route tracking SPA (une page view par navigation logique React Router)
      enableAutoRouteTracking: true,
      // Corrélation front→API→SQL : propage le traceparent W3C vers le domaine API (adopté par l'API, validé)
      distributedTracingMode: DistributedTracingModes.W3C,
      enableCorsCorrelation: true,
      ...(domains ? { correlationHeaderDomains: domains } : {}),
      disableFetchTracking: false,
      // Exceptions JS non capturées : auto-collectées par défaut (disableExceptionTracking non activé).
    },
  })
  instance.loadAppInsights()

  instance.addTelemetryInitializer(preparerItemTelemetrie)

  // Assigné en dernier : si une étape ci-dessus lève, le module reste sans client plutôt qu'avec un
  // client à moitié construit (fail-closed — la télémétrie se tait, l'application vit).
  appInsights = instance
}

/** Nom d'une vue de page dont la page n'a pas de libellé connu — jamais le titre de l'onglet. */
const NOM_VUE_SANS_LIBELLE = 'Page sans libellé'

/**
 * Initialiseur de télémétrie : (1) stampe l'accountId interne sur chaque item ; (2) retire la chaîne de requête des
 * adresses, de la page courante (`uri`, `url`) **et de la page précédente** (`refUri`, spec 160, FR-030 — elle partait
 * entière, `?user=<objectId Entra>` compris) ; (3) nomme une vue de page par le **seul libellé de page** (spec 160,
 * contrainte G4) : le SDK prend sinon `document.title`, qui porte désormais le nom du client. Ne lève jamais.
 */
export function preparerItemTelemetrie(item: ITelemetryItem): boolean {
  try {
    if (currentAccountId) {
      item.data = item.data ?? {}
      item.data['accountId'] = currentAccountId
    }
    const baseData = item.baseData as { uri?: string; url?: string; refUri?: string; target?: string; name?: string } | undefined
    if (baseData?.uri) baseData.uri = baseData.uri.split('?')[0]
    if (baseData?.url) baseData.url = baseData.url.split('?')[0]
    if (baseData?.refUri) baseData.refUri = baseData.refUri.split('?')[0]
    // La vue de page ET sa mesure de performance (`AppBrowserTimings`, une par chargement) : le SDK nomme l'une et
    // l'autre d'après `document.title` (revue à froid du 2026-10-11 pour la seconde).
    if (baseData && (item.baseType === PageView.dataType || item.baseType === PageViewPerformance.dataType)) {
      baseData.name = libellePageCourant() ?? NOM_VUE_SANS_LIBELLE
    }
    // Appel au serveur (dépendance) : le SDK y pose l'adresse COMPLÈTE, dans `target` et dans `name` (« GET /api/…?q=… »),
    // dont le texte tapé dans la recherche du bandeau — spec 160, élargissement accordé au grill-me du 2026-10-11.
    // `RemoteDependencyData.dataType` ; la constante n'est pas exportée par `applicationinsights-web`.
    if (baseData && item.baseType === 'RemoteDependencyData') {
      if (baseData.target) baseData.target = baseData.target.split('?')[0]
      if (baseData.name) baseData.name = baseData.name.split('?')[0]
    }
    // Spec 093 — version exécutée du frontend sur toute la télémétrie RUM (→ application_Version).
    const tags = (item.tags ?? {}) as Record<string, unknown>
    tags['ai.application.ver'] = __APP_VERSION__ || '0.0.0-dev'
    item.tags = tags as typeof item.tags
  } catch {
    // no-op — telemetry must never break the UI
  }
  return true
}

/** Pose l'accountId interne (corrélation) — appelé après résolution du profil. No-op si non initialisé. */
export function setTelemetryAccount(accountId: string): void {
  currentAccountId = accountId
}

function reportVital(metric: Metric): void {
  try {
    appInsights?.trackMetric({ name: `webvital.${metric.name}`, average: metric.value })
  } catch {
    // no-op — telemetry must never break the UI
  }
}

/** Core Web Vitals (LCP / INP / CLS) → metrics App Insights. Échantillonnage 100 % (trafic B2B faible). */
function trackWebVitals(): void {
  try {
    onLCP(reportVital)
    onINP(reportVital)
    onCLS(reportVital)
  } catch {
    // no-op — telemetry must never break the UI
  }
}

export function trackAuthEvent(name: FrontendAuthEvent, props: FrontendAuthEventProps): void {
  try {
    appInsights?.trackEvent({ name }, props)
  } catch {
    // no-op — telemetry must never throw
  }
}
